import {
  TemplateStructure,
  V1TemplateStructure,
  TEMPLATE_VERSION_V2,
  LabelElement,
  isAdvancedTableElement,
  PageSize,
  AdvancedTableData,
  AdvancedTableCellData,
  AdvancedTableElementProps,
} from '../types';
import { SalesOrder, SalesOrderItem } from 'services/salesOrders';
import Konva from 'konva';
import { getSOValueAtPath } from '../helpers';
import { getAdvancedTableCellDisplayValue } from '../helpers/tableHelpers';

// Module-level singleton for text measurement to optimize performance
// This instance is reused across all calculateActualRowHeights calls to avoid
// repeated Konva.Text instantiation overhead. Properties are explicitly set
// before each measurement to prevent cross-call pollution.
const textMeasurer = new Konva.Text({
  padding: 5,
  wrap: 'word',
});

/**
 * Ensures that any template, old or new, is converted to the new multi-page format.
 * This guarantees a consistent data structure for rendering and saving.
 *
 * @param templateData The template data, which can be in the old V1 or new V2 format.
 * @returns A template object conforming to the new `TemplateStructure`.
 */
export const normalizeTemplate = (
  templateData: TemplateStructure | V1TemplateStructure
): TemplateStructure => {
  // If it already has 'pages', it's the new format. Just ensure margins and footer settings are defaulted.
  if ('pages' in templateData) {
    return {
      ...templateData,
      marginTop: templateData.marginTop ?? 0,
      marginBottom: templateData.marginBottom ?? 0,
      marginLeft: templateData.marginLeft ?? 0,
      marginRight: templateData.marginRight ?? 0,
      footerEnabled: templateData.footerEnabled ?? false,
      footerHeight: templateData.footerHeight ?? 80,
    };
  }

  // If it's the legacy V1 format, convert it.
  return {
    version: TEMPLATE_VERSION_V2,
    pages: [{ elements: templateData.elements }],
    headerElements: [],
    footerElements: [],
    pageSize: templateData.pageSize,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    footerEnabled: false,
    footerHeight: 80,
  };
};

/**
 * Returns an array of elements with footer elements marked
 *
 * @param elements Array of label elements to check
 * @param pageSize The page size configuration
 * @param footerHeight Height of the footer in pixels
 * @param totalPages Total number of pages in the template
 * @returns Element array containing the elements in the footer area
 */
export const markedFooterElements = (
  elements: LabelElement[],
  pageSize: PageSize,
  footerHeight: number,
  totalPages: number
): LabelElement[] => {
  const singlePageHeight = pageSize.height;

  const markedElements: LabelElement[] = elements.map((element) => {
    const pageY = (totalPages - 1) * singlePageHeight;
    const footerStartY = pageY + singlePageHeight - footerHeight;

    // Defensively handle missing heights to prevent NaN comparisons
    const height = Number.isFinite(element.height) ? element.height : 0;

    // Check if element intersects with footer area (only for elements with valid height)
    const elementBottom = element.y + height;
    if (element.y >= footerStartY || elementBottom >= footerStartY) {
      return { ...element, footerElement: true };
    }
    return element;
  });

  return markedElements;
};

/**
 * Detects if the footer area is currently intersecting a table
 *
 * @param elements Array of LabelElements
 * @param footerPage Page number footer is on
 * @param footerHeight Height of the footer in pixels
 * @param pageSize The page size configuration
 * @returns Boolean indicating if table intersects footer
 */
export const footerIntersectsTable = (
  elements: LabelElement[],
  footerPage: number,
  footerHeight: number,
  pageSize: PageSize
): boolean => {
  const tableElements = elements.filter(
    (element) => element.type === 'advancedTable'
  );

  //get the footer y range
  const footerStartY = (footerPage + 1) * pageSize.height - footerHeight;
  const footerEndY = (footerPage + 1) * pageSize.height;

  let footerIntersects = false;

  tableElements.forEach((table) => {
    const tableEndY = table.y + (table.previewHeight || table.height);
    if (footerStartY < tableEndY && tableEndY <= footerEndY) {
      footerIntersects = true;
    }
  });
  return footerIntersects;
};

/**
 * Checks if there is room for a footer on the last page of the canvas
 * by examining only advancedTable elements that can grow dynamically
 *
 * @param footerHeight Height of footer in pixels
 * @param totalPages Number of pages
 * @param pageSize The page size configuration
 * @param elements Array of label elements
 * @returns Boolean indicating if there's room for the footer
 */
export const lastPageHasRoomForFooter = (
  footerHeight: number,
  totalPages: number,
  pageSize: PageSize,
  elements: LabelElement[]
): boolean => {
  // Filter to only advanced table elements since they can grow
  const advancedTableElements = elements.filter((element) =>
    isAdvancedTableElement(element)
  );

  // Calculate footer area boundaries on the last page
  const lastPageStartY = (totalPages - 1) * pageSize.height;
  const footerStartY = lastPageStartY + pageSize.height - footerHeight;

  // Check if any advanced table element would conflict with the footer area
  for (const table of advancedTableElements) {
    const tableBottomY = table.y + (table.previewHeight || table.height);

    // If table extends into or beyond the footer area, there's no room
    if (tableBottomY > footerStartY) {
      return false;
    }
  }

  return true;
};

/**
 * Updates footer elements' Y position to be at the bottom of the last page.
 * This function should be called after all pages have been created.
 *
 * @param elements Array of footer elements to update
 * @param pageSize The page size configuration
 * @param totalPages Total number of pages in the template
 * @param footerPage Page which contains the footer
 * @returns Updated footer elements with correct Y positions
 */
export const elementsWithUpdatedFooterPositionsOnLastPage = (
  elements: LabelElement[],
  pageSize: PageSize,
  totalPages: number,
  footerPage: number
): LabelElement[] => {
  const numberOfPagesToMove =
    totalPages > 1 ? totalPages - (footerPage + 1) : 0;

  return elements.map((element) => {
    if (element.footerElement) {
      return {
        ...element,
        y: element.y + numberOfPagesToMove * pageSize.height,
      };
    } else return element;
  });
};

/**
 * Find which page contains the first footer element.

 * @param {LabelElement[]} elements - Array of LabelElements
 * @param {PageSize} pageSize - Page size object.
 * @param {number} totalPages - Total number of pages.
 * @returns {number} 0-based page number containing the first footer element; `0` if none found.
 *
 */
export function findFooterPage(
  elements: LabelElement[],
  pageSize: PageSize,
  totalPages: number
): number {
  for (const element of elements) {
    if (!element?.footerElement) continue;

    const rawIndex = Math.floor(element.y / pageSize.height);
    return Math.max(0, Math.min(totalPages - 1, rawIndex));
  }

  return 0;
}

/**
 * Configuration for preview layout generation
 */
export interface PreviewLayoutConfig {
  pageSize: PageSize;
  showFooter: boolean;
  footerHeight: number;
  initialTotalPages: number;
}

/**
 * Result of preview layout generation
 */
export interface PreviewLayoutResult {
  elements: LabelElement[];
  totalPages: number;
  footerPage: number;
}

/**
 * Computes the total height of a split table by summing row heights and header height.
 *
 * @param rowHeights - Array of individual row heights in pixels
 * @param headerRowHeight - Optional height of the header row in pixels (defaults to 0)
 * @returns The total calculated height of the table in pixels
 */
const computeSplitTableHeight = (
  rowHeights: number[],
  headerRowHeight?: number
): number => {
  const totalRowHeight = rowHeights.reduce((acc, row) => acc + row, 0);
  return totalRowHeight + (headerRowHeight || 0);
};

/**
 * Calculates the actual row heights for table rows considering text wrapping and font properties.
 * This function measures text content to determine how much vertical space each row needs
 * when text may wrap within column width constraints.
 *
 * @param tableData - The advanced table data containing rows, headers, and formatting options
 * @param columnWidths - Array of column widths in pixels used for text wrapping calculations
 * @returns Array of calculated row heights in pixels, accounting for text wrapping
 */
const calculateActualRowHeights = (
  tableData: AdvancedTableData,
  columnWidths: number[]
): number[] => {
  const {
    data,
    headers,
    defaultCellFontFamily = 'Arial',
    defaultCellFontSize = 12,
    autoRowHeight,
    rowHeights: initialRowHeights,
  } = tableData;

  if (!autoRowHeight || !headers || !data) {
    return initialRowHeights;
  }

  const MIN_ROW_HEIGHT = 20;

  return data.map((row) => {
    let maxCellHeight = MIN_ROW_HEIGHT;
    if (row) {
      row.forEach((cell, colIndex) => {
        const columnWidth = columnWidths[colIndex];
        if (cell && columnWidth) {
          // Reset and configure the singleton textMeasurer for this measurement
          // All properties are explicitly set to avoid cross-call pollution
          textMeasurer.setAttrs({
            text: getAdvancedTableCellDisplayValue(
              cell.content,
              headers[colIndex]?.key,
              true // previewMode
            ),
            width: columnWidth,
            fontFamily: cell.fontFamily || defaultCellFontFamily,
            fontSize: cell.fontSize || defaultCellFontSize,
          });
          maxCellHeight = Math.max(maxCellHeight, textMeasurer.height());
        }
      });
    }
    return maxCellHeight;
  });
};

/**
 * Creates split table instances when a table is too large to fit on a single page.
 * This function analyzes table dimensions and distributes rows across multiple pages
 * while preserving table structure and header information on each page.
 *
 * @param originalElement - The original table element to be split
 * @param tableData - The advanced table data containing rows, headers, and configuration
 * @param pageHeight - The height of each page in pixels
 * @returns Array of table elements, each positioned on appropriate pages with correct row distributions
 */
const createSplitTables = (
  originalElement: LabelElement,
  tableData: AdvancedTableData,
  pageHeight: number
): AdvancedTableElementProps[] => {
  if (!isAdvancedTableElement(originalElement) || !tableData.data) {
    return [originalElement as AdvancedTableElementProps];
  }

  const { data, columnWidths, headerRow, headerRowHeight = 30 } = tableData;
  const headerHeight = headerRow ? headerRowHeight : 0;

  // Calculate actual row heights considering text wrapping
  const actualRowHeights = calculateActualRowHeights(tableData, columnWidths);

  // Calculate available height on first page considering table position
  const tablePagePosition = originalElement.y % pageHeight;
  const availableHeightOnFirstPage = pageHeight - tablePagePosition;

  // Calculate total table height using actual row heights
  const totalDataHeight = actualRowHeights.reduce(
    (sum, height) => sum + height,
    0
  );
  const totalTableHeight = headerHeight + totalDataHeight;

  // If table fits on first page, return original with preview height
  if (totalTableHeight <= availableHeightOnFirstPage) {
    return [
      {
        ...originalElement,
        previewHeight: totalTableHeight,
        advancedTableData: tableData,
      } as AdvancedTableElementProps,
    ];
  }

  const splitTables: AdvancedTableElementProps[] = [];
  let currentRowIndex = 0;
  let currentPageHeight = headerHeight;
  let pageIndex = 0;

  while (currentRowIndex < data.length) {
    const isFirstPage = pageIndex === 0;
    const availableHeight = isFirstPage
      ? availableHeightOnFirstPage
      : pageHeight;
    const pageStartRowIndex = currentRowIndex;

    // Find how many rows fit on this page
    while (currentRowIndex < data.length) {
      const nextRowHeight = actualRowHeights[currentRowIndex];

      // Check if row fits without tolerance to prevent cutoff
      if (
        currentPageHeight + nextRowHeight > availableHeight &&
        currentRowIndex > pageStartRowIndex
      ) {
        break; // This row won't fit
      }

      currentPageHeight += nextRowHeight;
      currentRowIndex++;
    }

    // Create table for this page
    const pageData = data.slice(pageStartRowIndex, currentRowIndex);
    const pageRowHeights = actualRowHeights.slice(
      pageStartRowIndex,
      currentRowIndex
    );

    const splitTableData: AdvancedTableData = {
      ...tableData,
      data: pageData,
      rowHeights: pageRowHeights,
      numRows: pageData.length,
      headerRow: tableData.headerRow,
      headers: tableData.headers,
    };

    // Calculate Y position for this page
    const currentPageNumber = Math.floor(originalElement.y / pageHeight);
    const splitTableY = isFirstPage
      ? originalElement.y
      : (currentPageNumber + pageIndex) * pageHeight;

    const splitTableHeight = computeSplitTableHeight(
      splitTableData.rowHeights,
      splitTableData.headerRowHeight
    );

    const splitTable: AdvancedTableElementProps = {
      ...originalElement,
      id: isFirstPage
        ? originalElement.id
        : `${originalElement.id}_page_${pageIndex}`,
      y: splitTableY,
      advancedTableData: splitTableData,
      previewHeight: splitTableHeight,
    };

    splitTables.push(splitTable);

    // Reset for next page
    currentPageHeight = headerHeight; // Next page starts with header
    pageIndex++;
  }

  return splitTables;
};

/**
 * Generates the complete preview layout with all automatic positioning, table splitting,
 * and footer management logic consolidated into a single pure function.
 *
 * @param originalElements - The original elements array from the template
 * @param salesOrder - Sales order data for preview
 * @param config - Configuration for layout generation
 * @returns Complete layout result with elements ready for preview mode
 */
export const generatePreviewLayout = (
  originalElements: LabelElement[],
  salesOrder: SalesOrder | null,
  config: PreviewLayoutConfig
): PreviewLayoutResult => {
  const { pageSize, showFooter, footerHeight, initialTotalPages } = config;
  let totalPages = initialTotalPages;
  const allSplitElements: LabelElement[] = [];

  // Step 1: Mark footer elements first if footer is enabled
  let currentElements = [...originalElements];
  if (showFooter) {
    currentElements = markedFooterElements(
      currentElements,
      pageSize,
      footerHeight,
      totalPages
    );
  }

  // Step 2: Process advanced table elements with sales order data
  const processedElements: LabelElement[] = [];

  for (const element of currentElements) {
    if (isAdvancedTableElement(element) && salesOrder?.salesItems) {
      const originalTableData = element.advancedTableData;
      if (!originalTableData) {
        processedElements.push(element);
        continue;
      }

      const headers = originalTableData.headers;
      if (!headers) {
        processedElements.push(element);
        continue;
      }

      // Create table data from sales order items
      const tableData: AdvancedTableCellData[][] = [];
      salesOrder.salesItems.forEach((item: SalesOrderItem) => {
        const rowData: AdvancedTableCellData[] = [];
        headers.forEach((header) => {
          const content = getSOValueAtPath(
            item,
            header.path || header.key || ''
          );
          rowData.push({
            content: content?.toString() ?? '',
          });
        });
        tableData.push(rowData);
      });

      const newDisplayData: AdvancedTableData = {
        ...originalTableData,
        numRows: salesOrder.salesItems.length,
        numColumns: headers.length,
        rowHeights: Array(salesOrder.salesItems.length).fill(
          originalTableData.rowHeights?.[0] || 18
        ),
        data: tableData,
      };

      // Step 3: Split tables across pages
      const splitTables = createSplitTables(
        element,
        newDisplayData,
        pageSize.height
      );

      // Add the first (original) table with updated data
      processedElements.push(splitTables[0]);

      // Collect additional split tables
      if (splitTables.length > 1) {
        allSplitElements.push(...splitTables.slice(1));
      }

      // Calculate required pages based on split tables
      const maxTableBottom = Math.max(
        ...splitTables.map((table) => {
          const tableHeight = table.previewHeight || table.height;
          return table.y + tableHeight;
        })
      );
      const requiredPages = Math.ceil(maxTableBottom / pageSize.height);
      totalPages = Math.max(totalPages, requiredPages);
    } else {
      processedElements.push(element);
    }
  }

  // Combine processed elements with split tables
  const allElements = [...processedElements, ...allSplitElements];

  // Step 4: Handle footer positioning and page management
  let finalElements = allElements;
  let finalTotalPages = totalPages;
  let finalFooterPage = totalPages - 1; // Default to last page

  if (showFooter) {
    // Find where footer elements are currently located
    const actualFooterPage = findFooterPage(allElements, pageSize, totalPages);

    // Check if footer needs to be moved to last page
    if (actualFooterPage !== totalPages - 1) {
      if (
        lastPageHasRoomForFooter(
          footerHeight,
          totalPages,
          pageSize,
          allElements
        )
      ) {
        // Move footer to last page
        finalElements = elementsWithUpdatedFooterPositionsOnLastPage(
          allElements,
          pageSize,
          totalPages,
          actualFooterPage
        );
      } else {
        // Add a new page and move footer there
        ++finalTotalPages;
        finalElements = elementsWithUpdatedFooterPositionsOnLastPage(
          allElements,
          pageSize,
          finalTotalPages,
          actualFooterPage
        );
      }
    } else {
      // Footer is on last page, check if it intersects with tables
      const intersects = footerIntersectsTable(
        allElements,
        actualFooterPage,
        footerHeight,
        pageSize
      );

      if (intersects) {
        // Add a new page and move footer there
        ++finalTotalPages;
        finalElements = elementsWithUpdatedFooterPositionsOnLastPage(
          allElements,
          pageSize,
          finalTotalPages,
          actualFooterPage
        );
      }
    }

    // Update final footer page
    finalFooterPage = findFooterPage(finalElements, pageSize, finalTotalPages);
  }

  return {
    elements: finalElements,
    totalPages: finalTotalPages,
    footerPage: finalFooterPage,
  };
};
